///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Analysis behind the storm column, all pure: the brightness-temperature
/// to cloud-top-height conversion the lid and the wall sections share, the
/// parametric surface wind model fitted to the advisory radii, integrated
/// kinetic energy, the IR structure statistics (coldest top, deep
/// convection fraction, eye diameter) and the ensemble intensity spread.
///

import { type EnsembleTracks, type StormTrackPoint, type WindRadii, type WindRadiiQuadrants } from "../HurricanesTable"

const cKmPerNm = 1.852
const cKtToMps = 0.514444

// ---------------------------------------------------------------------------
// Cloud-top height from the IR cold index
// ---------------------------------------------------------------------------

/** Brightness-temperature window encoded in the frames (T_WARM / T_COLD in process_storm_cloud.py) */
export const cCloudWarmK = 290
export const cCloudColdK = 200
/** Tops warmer than this are indistinguishable from the sea surface in IR */
export const cCloudClearK = 280
/** Tops at or below this read as fully developed cloud */
export const cCloudThickK = 235
/** Tropical sea-surface air temperature the lapse rate descends from */
export const cSurfaceK = 300
/** Moist tropical lapse rate, K per km */
export const cLapseKPerKm = 6.5

/** Cold index (0..1) to brightness temperature, kelvin */
export function coldIndexToKelvin(v: number): number {
	return cCloudWarmK - (cCloudWarmK - cCloudColdK) * Math.min(1, Math.max(0, v))
}

/** Brightness temperature to lapse-rate cloud-top height, km (0 at the surface) */
export function kelvinToTopKm(kelvin: number): number {
	return Math.max(0, (cSurfaceK - kelvin) / cLapseKPerKm)
}

/**
 * Lid height for a cold index: the lapse-rate height weighted by how much
 * of the pixel is cloud (clear → thick ramp), so the clear sea reads as
 * zero instead of a 1.5 km floor. Keep in sync with stormColumnTopKm in
 * storm_column_lid.glsl
 */
export function cloudTopKm(coldIndex: number): number {
	const t = coldIndexToKelvin(coldIndex)
	const thickness = smoothstep(cCloudClearK, cCloudThickK, t)
	return kelvinToTopKm(t) * thickness
}

function smoothstep(e0: number, e1: number, x: number): number {
	const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
	return t * t * (3 - 2 * t)
}

// ---------------------------------------------------------------------------
// Parametric surface wind
// ---------------------------------------------------------------------------

/** Quadrant order used throughout: NE, SE, SW, NW (bearings 45°, 135°, 225°, 315°) */
export const cQuadrantKeys: Array<keyof WindRadiiQuadrants> = ["ne", "se", "sw", "nw"]
const cQuadrantBearings = [45, 135, 225, 315].map(d => d * Math.PI / 180)

export interface WindModel {
	vmaxKt: number
	rmwKm: number
	/** Holland B per quadrant (NE, SE, SW, NW); larger = peakier profile */
	b: [number, number, number, number]
	/** "fit": B solved from the advisory radii; "climatology": no radii, symmetric default */
	source: "fit" | "climatology"
	/** "atcf": RMW from the NHC best track; "climatology": Knaff et al. 2015 from Vmax and latitude */
	rmwSource: "atcf" | "climatology"
}

/**
 * Climatological radius of maximum wind (Knaff et al. 2015), km, from the
 * intensity and latitude. Advisories don't carry the RMW; this stands in
 * so the profile has a peak to hang the radii fit on
 */
export function climatologicalRmwKm(vmaxKt: number, latDeg: number): number {
	const v = Math.max(20, vmaxKt)
	const rmw = 218.3714 - 1.2014 * v + Math.pow(v / 10.9844, 2) - Math.pow(v / 35.3052, 3) - 145.509 * Math.cos(latDeg * Math.PI / 180)
	return Math.min(120, Math.max(12, rmw))
}

/** Holland (1980) profile shape: V/Vmax as a function of x = (RMW / r)^B */
function hollandShape(x: number): number {
	return Math.sqrt(x * Math.exp(1 - x))
}

/**
 * Invert the Holland shape for x in (0, 1] (outside the RMW), where it is
 * monotonic increasing: find x with x·e^(1−x) = y
 */
function invertHollandShape(y: number): number | undefined {
	if (y <= 0 || y >= 1) return undefined
	let lo = 1e-6
	let hi = 1
	for (let i = 0; i < 48; i++) {
		const mid = (lo + hi) / 2
		if (mid * Math.exp(1 - mid) < y) lo = mid
		else hi = mid
	}
	return (lo + hi) / 2
}

/**
 * Fit the Holland profile per quadrant to the advisory's 34/50/64 kt
 * radii, with the RMW from the best track when given, else climatology.
 * Each radius gives one B; a quadrant's B is the mean of its thresholds,
 * clamped to the physical range. Quadrants without radii borrow the mean
 * of the others; with no radii at all the model is symmetric with a
 * climatological B
 */
export function fitWindModel(vmaxKt: number | null, latDeg: number, radii: WindRadii | null, observedRmwKm?: number | null): WindModel | undefined {
	if (vmaxKt === null || !Number.isFinite(vmaxKt) || vmaxKt < 25) return undefined
	const observed = observedRmwKm !== null && observedRmwKm !== undefined && observedRmwKm >= 5 && observedRmwKm <= 250
	const rmwKm = observed ? observedRmwKm : climatologicalRmwKm(vmaxKt, latDeg)
	const rmwSource: WindModel["rmwSource"] = observed ? "atcf" : "climatology"
	const defaultB = 1.3 + 0.4 * Math.min(1, Math.max(0, (vmaxKt - 50) / 90))

	const perQuadrant: Array<number | undefined> = cQuadrantKeys.map(key => {
		const samples: number[] = []
		for (const [thresholdStr, quadrants] of Object.entries(radii ?? {}) as Array<[string, WindRadiiQuadrants | undefined]>) {
			const threshold = Number(thresholdStr)
			const nm = quadrants?.[key]
			if (!nm || nm <= 0 || threshold >= vmaxKt) continue
			const rKm = nm * cKmPerNm
			if (rKm <= rmwKm * 1.2) continue
			const x = invertHollandShape(Math.pow(threshold / vmaxKt, 2))
			if (x === undefined) continue
			const b = Math.log(x) / Math.log(rmwKm / rKm)
			if (Number.isFinite(b)) samples.push(Math.min(2.6, Math.max(0.7, b)))
		}
		return samples.length > 0 ? samples.reduce((s, v) => s + v, 0) / samples.length : undefined
	})

	const fitted = perQuadrant.filter((b): b is number => b !== undefined)
	if (fitted.length === 0) {
		return { vmaxKt, rmwKm, b: [defaultB, defaultB, defaultB, defaultB], source: "climatology", rmwSource }
	}
	const mean = fitted.reduce((s, v) => s + v, 0) / fitted.length
	const b = perQuadrant.map(v => v ?? mean) as [number, number, number, number]
	return { vmaxKt, rmwKm, b, source: "fit", rmwSource }
}

/** Holland B at a bearing, blended smoothly between the quadrant centers */
export function hollandBAt(model: WindModel, bearingRad: number): number {
	// Weighted by a raised cosine of the angular distance to each quadrant center
	let sum = 0
	let wsum = 0
	for (let q = 0; q < 4; q++) {
		let d = Math.abs(bearingRad - cQuadrantBearings[q]) % (2 * Math.PI)
		if (d > Math.PI) d = 2 * Math.PI - d
		const w = Math.pow(Math.max(0, Math.cos(d / 2)), 4)
		sum += w * model.b[q]
		wsum += w
	}
	return wsum > 0 ? sum / wsum : model.b[0]
}

/** Modeled sustained wind, kt, at a distance and compass bearing from the center */
export function hollandWindKt(model: WindModel, distKm: number, bearingRad: number): number {
	if (distKm < 0.5) return 0
	const b = hollandBAt(model, bearingRad)
	const x = Math.pow(model.rmwKm / distKm, b)
	return model.vmaxKt * hollandShape(x)
}

/**
 * Integrated kinetic energy (Powell & Reinhold 2007) over the modeled
 * field where the wind is at least 10 m/s, in terajoules: ½ρV² summed
 * over the area. A size-aware damage-potential number Vmax alone misses
 */
export function integratedKineticEnergyTJ(model: WindModel): number {
	const rho = 1.15
	const dr = 4
	const nTheta = 48
	let joules = 0
	for (let r = dr / 2; r < 1200; r += dr) {
		let ring = 0
		let any = false
		for (let i = 0; i < nTheta; i++) {
			const bearing = (i + 0.5) * 2 * Math.PI / nTheta
			const v = hollandWindKt(model, r, bearing) * cKtToMps
			if (v >= 10) {
				ring += 0.5 * rho * v * v
				any = true
			}
		}
		if (!any && r > model.rmwKm * 2) break
		// Cell area: annulus sector, m²
		const area = (2 * Math.PI * r * dr / nTheta) * 1e6
		joules += ring * area
	}
	return joules / 1e12
}

/** Mean and asymmetry (max/min) of the 34-kt radii, km; undefined without radii */
export function radius34Summary(radii: WindRadii | null): { meanKm: number; asymmetry: number } | undefined {
	const q = radii?.["34"]
	if (!q) return undefined
	const values = cQuadrantKeys.map(k => q[k]).filter(v => v > 0)
	if (values.length === 0) return undefined
	const meanKm = values.reduce((s, v) => s + v, 0) / values.length * cKmPerNm
	return { meanKm, asymmetry: Math.max(...values) / Math.min(...values) }
}

// ---------------------------------------------------------------------------
// IR structure
// ---------------------------------------------------------------------------

/** A decoded storm-centered cold-index grid */
export interface ColdIndexGrid {
	/** Pixels per side */
	size: number
	/** Row-major, north row first, values 0..255 */
	data: Uint8Array
	minLon: number
	minLat: number
	maxLon: number
	maxLat: number
}

/** Bilinear cold index (0..1) at a lat/lon; 0 outside the box */
export function sampleColdIndex(grid: ColdIndexGrid, lat: number, lon: number): number {
	let dLon = lon - grid.minLon
	if (dLon < -180) dLon += 360
	if (dLon > 180) dLon -= 360
	const u = dLon / (grid.maxLon - grid.minLon)
	const v = (grid.maxLat - lat) / (grid.maxLat - grid.minLat)
	if (u < 0 || u > 1 || v < 0 || v > 1) return 0
	const n = grid.size
	const fx = Math.min(n - 1.001, Math.max(0, u * (n - 1)))
	const fy = Math.min(n - 1.001, Math.max(0, v * (n - 1)))
	const x0 = Math.floor(fx)
	const y0 = Math.floor(fy)
	const tx = fx - x0
	const ty = fy - y0
	const d = grid.data
	const a = d[y0 * n + x0]
	const b = d[y0 * n + x0 + 1]
	const c = d[(y0 + 1) * n + x0]
	const e = d[(y0 + 1) * n + x0 + 1]
	return ((a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + e * tx) * ty) / 255
}

export interface IrStructure {
	coldestTopC: number
	coldestTopKm: number
	/** Fraction of the frame colder than -70 °C */
	coldFraction: number
	eyeDiameterKm?: number
	eyeContrastK?: number
}

/** Deep-convection threshold, kelvin (-70 °C) */
const cDeepConvectionK = 203.15

/**
 * Statistics of one frame: the coldest top, the deep-convection fraction
 * and, when a warm core sits inside a cold ring near the frame center (the
 * storm's position at scan time), the eye diameter from the radial
 * brightness-temperature profile
 */
export function analyzeColdIndexGrid(grid: ColdIndexGrid): IrStructure {
	const n = grid.size
	const d = grid.data
	let maxV = 0
	let cold = 0
	const deepIndex = (cCloudWarmK - cDeepConvectionK) / (cCloudWarmK - cCloudColdK) * 255
	for (const v of d) {
		if (v > maxV) maxV = v
		if (v >= deepIndex) cold++
	}
	const coldestK = coldIndexToKelvin(maxV / 255)
	const result: IrStructure = {
		coldestTopC: coldestK - 273.15,
		coldestTopKm: kelvinToTopKm(coldestK),
		coldFraction: cold / d.length
	}

	// Radial profile about the frame center, 1 px bins out to ~150 km
	const centerLat = (grid.minLat + grid.maxLat) / 2
	const centerLon = (grid.minLon + grid.maxLon) / 2
	const kmPerPxNs = (grid.maxLat - grid.minLat) * 111.32 / n
	const kmPerPxEw = (grid.maxLon - grid.minLon) * 111.32 * Math.cos(centerLat * Math.PI / 180) / n
	const kmPerPx = (kmPerPxNs + kmPerPxEw) / 2
	const maxR = Math.min(Math.floor(150 / kmPerPx), Math.floor(n / 2) - 1)
	if (maxR < 6) return result
	const profile: number[] = []
	for (let r = 0; r <= maxR; r++) {
		const samples = Math.max(8, Math.round(2 * Math.PI * r))
		let sum = 0
		for (let s = 0; s < samples; s++) {
			const a = s * 2 * Math.PI / samples
			const lat = centerLat + (r * Math.sin(a) * kmPerPx) / 111.32
			const lon = centerLon + (r * Math.cos(a) * kmPerPx) / (111.32 * Math.cos(centerLat * Math.PI / 180))
			sum += coldIndexToKelvin(sampleColdIndex(grid, lat, lon))
		}
		profile.push(sum / samples)
	}
	// Eyewall: the coldest ring within ~90 km; the eye is a warmer center inside it
	let ringR = 1
	for (let r = 1; r <= Math.min(maxR, Math.floor(90 / kmPerPx)); r++) {
		if (profile[r] < profile[ringR]) ringR = r
	}
	const ringK = profile[ringR]
	const coreK = profile[0]
	const contrast = coreK - ringK
	if (ringR >= 2 && ringK <= 230 && contrast >= 8) {
		// Eye edge: where the profile falls halfway from the core to the ring
		const half = coreK - contrast / 2
		let edge = ringR
		for (let r = 0; r <= ringR; r++) {
			if (profile[r] <= half) {
				edge = r
				break
			}
		}
		result.eyeDiameterKm = Math.round(2 * edge * kmPerPx)
		result.eyeContrastK = Math.round(contrast)
	}
	return result
}

// ---------------------------------------------------------------------------
// Wind–pressure relationship
// ---------------------------------------------------------------------------

/** Climatological environmental pressure by basin, hPa (Knaff & Zehr 2007 defaults) */
export function environmentalPressureHpa(basin: string): number {
	switch (basin.toUpperCase()) {
		case "AL": return 1012
		case "EP": return 1010
		default: return 1008
	}
}

/**
 * Knaff & Zehr (2007) wind–pressure relationship: the minimum central
 * pressure a storm of this intensity "should" have, given its latitude and
 * translation speed, at climatological size. A storm well above the curve
 * is small and efficient; one well below has a large, lazy pressure field
 */
export function knaffZehrPressureHpa(vmaxKt: number, latDeg: number, translationKt: number, basin: string): number {
	const vsrm = Math.max(0, vmaxKt - 1.5 * Math.pow(Math.max(0, translationKt), 0.63))
	const size = 1
	return 23.286 - 0.483 * vsrm - Math.pow(vsrm / 24.254, 2) - 12.587 * size - 0.483 * Math.abs(latDeg) + environmentalPressureHpa(basin)
}

// ---------------------------------------------------------------------------
// Intensity series and ensemble spread
// ---------------------------------------------------------------------------

/** Saffir–Simpson category from sustained wind, kt (0 = below hurricane strength) */
export function categoryFromWindKt(kt: number): number {
	if (kt >= 137) return 5
	if (kt >= 113) return 4
	if (kt >= 96) return 3
	if (kt >= 83) return 2
	if (kt >= 64) return 1
	return 0
}

/** Sustained wind change over the last 24 h of the observed track, kt; null when the record is too short */
export function windChange24h(observed: StormTrackPoint[]): number | null {
	const withWind = observed.filter(p => p.wind_kt !== null)
	if (withWind.length < 2) return null
	const last = withWind[withWind.length - 1]
	const target = last.t - 24 * 3600_000
	if (withWind[0].t > target + 3 * 3600_000) return null
	let before = withWind[0]
	let after = withWind[0]
	for (const p of withWind) {
		if (p.t <= target) before = p
		if (p.t >= target) {
			after = p
			break
		}
	}
	const span = after.t - before.t
	const wind = span > 0
		? (before.wind_kt ?? 0) + ((after.wind_kt ?? 0) - (before.wind_kt ?? 0)) * (target - before.t) / span
		: (before.wind_kt ?? 0)
	return Math.round((last.wind_kt ?? 0) - wind)
}

export interface EnsembleSpread {
	p10: Array<[number, number]>
	p90: Array<[number, number]>
	categoryShare: Array<{ t: number; category: number; share: number; members: number }>
}

/**
 * Member wind percentiles per forecast hour and, at the standard taus, the
 * modal category among members with its share. Taus with fewer than half
 * the members still reporting are dropped (dissipated members bias the
 * spread low)
 */
export function ensembleIntensitySpread(ensemble: EnsembleTracks): EnsembleSpread | undefined {
	const runMs = Date.parse(ensemble.run)
	if (!Number.isFinite(runMs) || ensemble.members.length === 0) return undefined
	const byTau = new Map<number, number[]>()
	for (const member of ensemble.members) {
		for (const pt of member.pts) {
			const wind = pt[3]
			if (wind === null || !Number.isFinite(wind)) continue
			const list = byTau.get(pt[0]) ?? []
			list.push(wind)
			byTau.set(pt[0], list)
		}
	}
	const taus = [...byTau.keys()].sort((a, b) => a - b)
	const minMembers = Math.max(3, Math.ceil(ensemble.members.length / 2))
	const p10: Array<[number, number]> = []
	const p90: Array<[number, number]> = []
	const categoryShare: EnsembleSpread["categoryShare"] = []
	for (const tau of taus) {
		const winds = byTau.get(tau)!
		if (winds.length < minMembers) continue
		winds.sort((a, b) => a - b)
		const t = runMs + tau * 3600_000
		p10.push([t, percentile(winds, 0.1)])
		p90.push([t, percentile(winds, 0.9)])
		if (tau > 0 && tau % 24 === 0 && tau <= 120) {
			const counts = new Map<number, number>()
			for (const w of winds) {
				const c = w >= 34 ? categoryFromWindKt(w) : -1
				counts.set(c, (counts.get(c) ?? 0) + 1)
			}
			let modal = -1
			let modalCount = 0
			for (const [c, count] of counts) {
				if (count > modalCount || (count === modalCount && c > modal)) {
					modal = c
					modalCount = count
				}
			}
			categoryShare.push({ t, category: modal, share: modalCount / winds.length, members: winds.length })
		}
	}
	if (p10.length === 0) return undefined
	return { p10, p90, categoryShare }
}

function percentile(sorted: number[], q: number): number {
	const pos = (sorted.length - 1) * q
	const lo = Math.floor(pos)
	const hi = Math.min(sorted.length - 1, lo + 1)
	return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo)
}
